import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Canceler } from "axios";
import {
    useAppTable,
    useFormContainerState,
    useMessageRedirect,
    useTrans,
} from "../../../hooks";
import { VStack, HStack, useToast, Box } from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { AppSearch } from "../../../containers";
import {
    AppFilterButton,
    AppLoader,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { InternshipScheduleStudentList as ListComponent } from "./InternshipScheduleStudentList";
import { InternshipScheduleStudentFilter as FilterComponent } from "./InternshipScheduleStudentFilter";
import { InternshipScheduleStudentForm as FormComponent } from "./InternshipScheduleStudentForm";
import {
    Internship,
    InternshipApi,
    InternshipSchedule,
    InternshipScheduleApi,
    User as Entity,
    UserApi as EntityApi,
    InternshipScheduleSeatApi,
    InternshipScheduleSeat,
    School,
    TimesheetApi,
    Timesheet,
} from "../../../apis";
import { APPC, cPath } from "../../../config";

export const InternshipScheduleStudentPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const navigate = useNavigate();
    const { redirectMessageInternship } = useMessageRedirect();
    const {
        id: internshipId,
        internshipScheduleId,
        internshipScheduleSeatId,
    } = useParams<{
        id?: string;
        internshipScheduleId?: string;
        internshipScheduleSeatId?: string;
    }>();
    const formContainerState = useFormContainerState();

    // state & const
    const [search, setSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [internship, setInternship] = useState<Internship | null>(null);
    const [internshipSchedule, setInternshipSchedule] =
        useState<InternshipSchedule | null>(null);
    const [internshipScheduleSeat, setInternshipScheduleSeat] =
        useState<InternshipScheduleSeat>();
    const [isDetailPage, setIsDetailPage] = useState(false);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const isCompany =
        window.location.pathname ===
        cPath("ADMIN.INTERNSHIP_SCHEDULE_STUDENT_PAGE", {
            id: internshipId,
            internshipScheduleId: internshipScheduleId,
        });

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("firstName");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            [`order[${sortColumn}]`]: sortOrder,
            ...filterData,
        };
        let endPointName = "getCollectionSchedule";
        if (isCompany) {
            params["internshipStudents.internshipSchedule.id"] =
                internshipScheduleId;
        } else {
            params["internshipScheduleSeatId"] = internshipScheduleSeatId;
            endPointName = "getCollectionAssign";
        }
        if (search) {
            params["user_search"] = search;
        }

        await EntityApi[endPointName]<Entity>(pageIndex + 1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const downloadReportHandler = (
        userId: number,
        internshipScheduleId: number
    ) => {
        if (internshipScheduleId > 0) {
            TimesheetApi.postCollectionReport<BlobPart, Timesheet>({
                internshipSchedule:
                    InternshipScheduleApi.toResourceIRI(internshipScheduleId),
                user: EntityApi.toResourceIRI(userId),
            } as Timesheet).then(({ response }) => {
                if (response !== null) {
                    const fileURL = URL.createObjectURL(
                        new Blob([response], { type: "application/pdf" })
                    );
                    const alink = document.createElement("a");
                    alink.href = fileURL;
                    alink.download = "timesheet-report.pdf";
                    alink.click();
                }
            });
        }
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "skills":
                if (isCompany) {
                    navigate(
                        cPath("ADMIN.INTERNSHIP_SCHEDULE_STUDENT_SKILL_PAGE", {
                            id: internshipId,
                            internshipScheduleId: internshipScheduleId,
                            studentId: data?.id,
                        })
                    );
                } else {
                    navigate(
                        cPath("ADMIN.STUDENTS_SKILL_PAGE", {
                            internshipScheduleSeatId: internshipScheduleSeatId,
                            studentId: data?.id,
                        })
                    );
                }
                break;
            case "detail":
                setIsDetailPage(true);
                formContainerState.open(data?.id);
                break;
            case "message":
                redirectMessageInternship(
                    data,
                    internshipScheduleSeat
                        ? (internshipScheduleSeat.internshipSchedule as InternshipSchedule)
                        : (internshipSchedule as InternshipSchedule),
                    (data.schools?.[0] as School)?.id
                );
                break;
            case "timesheet-report":
                downloadReportHandler(
                    data.id,
                    internshipScheduleSeat
                        ? (
                              internshipScheduleSeat.internshipSchedule as InternshipSchedule
                          ).id
                        : (internshipSchedule as InternshipSchedule).id
                );
                break;
        }
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen) {
            fetchListData();
        }
    }, [
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        filterData,
        formContainerState.isOpen,
    ]);

    useEffect(() => {
        if (isCompany) {
            setLoading(true);
            Promise.all([
                internshipId
                    ? InternshipApi.getItem<Internship>(internshipId)
                    : Promise.resolve(null),
                internshipScheduleId
                    ? InternshipScheduleApi.getItem<InternshipSchedule>(
                          internshipScheduleId
                      )
                    : Promise.resolve(null),
            ])
                .then(([internshipRes, scheduleRes]) => {
                    if (internshipRes) {
                        if (internshipRes.errorMessage) {
                            toast({
                                title: internshipRes.errorMessage,
                                status: "error",
                            });
                        } else if (internshipRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (internshipRes.response !== null) {
                            setInternship(internshipRes.response);
                        }
                    }
                    if (scheduleRes) {
                        if (scheduleRes.errorMessage) {
                            toast({
                                title: scheduleRes.errorMessage,
                                status: "error",
                            });
                        } else if (scheduleRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (scheduleRes.response !== null) {
                            setInternshipSchedule(scheduleRes.response);
                        }
                    }
                })
                .finally(() => setLoading(false));
        } else {
            if (internshipScheduleSeatId) {
                setLoading(true);
                InternshipScheduleSeatApi.getItemMyInternship<InternshipScheduleSeat>(
                    internshipScheduleSeatId
                )
                    .then(({ errorMessage, isNotFound, response }) => {
                        if (errorMessage) {
                            toast({
                                title: errorMessage,
                                status: "error",
                            });
                        } else if (isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (response !== null) {
                            setInternshipScheduleSeat(response);
                        }
                    })
                    .finally(() => setLoading(false));
            }
        }
    }, [internshipId, internshipScheduleId, internshipScheduleSeatId]);

    useEffect(() => {
        if (pageIndex > 0) {
            table.dispatch(updatePageIndex(0));
        }
    }, [filterData]);

    if (loading) {
        return <AppLoader />;
    }

    return (
        <>
            <AppPageHeader
                title={t("padm.InternshipScheduleStudentPage:text.pageTitle")}
                breadcrumb={
                    internship?.name && internshipSchedule?.name
                        ? [
                              {
                                  label: internship?.name,
                                  url: cPath("ADMIN.INTERNSHIP_PAGE"),
                              },
                              {
                                  label: internshipSchedule?.name,
                                  url: cPath("ADMIN.INTERNSHIP_SCHEDULE_PAGE", {
                                      id: internshipId,
                                  }),
                              },
                          ]
                        : internshipScheduleSeat
                          ? [
                                {
                                    label: `${
                                        (
                                            (
                                                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                            )?.internship as Internship
                                        ).name
                                    } (${
                                        (
                                            internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                        ).name
                                    })`,
                                    url: cPath("ADMIN.MY_INTERNSHIP_PAGE"),
                                },
                            ]
                          : []
                }
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    <VStack p={6} alignItems={"flex-start"} gap={4}>
                        <ListComponent
                            table={table}
                            paging={paging}
                            list={list}
                            loadingList={loadingList}
                            onActionClick={actionClickHandler}
                        />
                    </VStack>
                    {formContainerState.isOpen && (
                        <FormComponent
                            formContainerState={formContainerState}
                            isDetailPage={isDetailPage}
                            internshipSchedule={
                                internshipScheduleSeat
                                    ? (internshipScheduleSeat.internshipSchedule as InternshipSchedule)
                                    : internshipSchedule
                            }
                        />
                    )}
                </>
            </AppPageContent>
        </>
    );
};
